import React from "react";
import { QueryClient, QueryClientProvider, QueryKey } from "react-query";
import { BackendResponse } from "./BackendResponse";
import { incrementValueVersion } from "../utils/incrementValueVersion";

export interface CvpQueryClient {
  /** stall updates of already fetched queries to avoid unnecessary UI updates */
  stallUpdates: boolean;
  queryClient: QueryClient;
  invalidateQueries: QueryClient["invalidateQueries"];
}
export const CvpQueryClientContext = React.createContext<CvpQueryClient>(null);
export function useCvpQueryClient() {
  return React.useContext(CvpQueryClientContext);
}

export const queryClient = getQueryClient();

export const CvpQueryClientProvider = ({ children }) => {
  const [stallUpdates, setStallUpdates] = React.useState(false);

  return (
    <CvpQueryClientContext.Provider
      value={{
        stallUpdates: stallUpdates,
        queryClient,
        invalidateQueries: async (...args) => {
          setStallUpdates(true);
          const result = await queryClient.invalidateQueries(...args);
          setStallUpdates(false);
          return result;
        },
      }}
    >
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </CvpQueryClientContext.Provider>
  );
};

function getQueryClient() {
  const queryClient = new QueryClient();
  try {
    hydrateQueryClient(queryClient);
  } catch (e) {
    console.error("hydrateQueryClient failed", e);
  }
  return queryClient;
}

// hydrate the QueryClient with the data from the server
// then we dont have to wait for api calls like whoAmI before render
function hydrateQueryClient(queryClient: QueryClient) {
  const getReactQueryData = (window as any).getReactQueryData;
  if (!getReactQueryData) return;

  const queryData = getReactQueryData();
  for (const key in queryData) {
    const data = queryData[key];

    const response: BackendResponse = {
      valueVersion: incrementValueVersion(),
      statusCode: 200,
      ok: true,
      ...data,
    };

    queryClient.setQueryData([key], response);
  }
}
